import React from 'react';
import PropTypes from 'prop-types';

import LoadingButton from '@mui/lab/LoadingButton';

import SquareDotsSpinner from '../components/SquareDotsSpinner';

/**
 * ActionButton — the project's canonical action-button component.
 *
 * Wraps MUI Lab's <LoadingButton> with two project-wide defaults so the
 * in-flight visual stays uniform across every action surface in the app:
 *
 *   1. `loadingIndicator` defaults to <SquareDotsSpinner size="small">,
 *      replacing MUI's generic CircularProgress with the WARA-Ops dotted-
 *      square "chasing tail". The dots use `currentcolor`, so they inherit
 *      whatever text colour the surrounding button context provides — no
 *      hard-coded hue, no per-callsite override needed. Marked
 *      aria-hidden because LoadingButton already announces the loading
 *      state via `aria-busy` on the <button> itself; without aria-hidden,
 *      assistive tech would hear a duplicate "Loading" status from the
 *      spinner's own role=status.
 *
 *   2. `loadingPosition` defaults to "start" — always, regardless of
 *      whether the caller passed a `startIcon`. Anchoring the indicator
 *      to its own slot before the label means the spinner can never
 *      overlap the button text (MUI Lab's default would centre the
 *      indicator OVER the children, which produces an unreadable overlap
 *      on labels like "Opening Argo…").
 *
 *      Catch: MUI Lab's "start" mode silently falls back to a centred
 *      overlay if there's no `startIcon` slot to anchor to (a 0×0 hidden
 *      span is enough). We therefore inject a hidden placeholder
 *      whenever the caller didn't pass a real startIcon. The placeholder
 *      is sized 0×0 at idle so the button width doesn't change; while
 *      loading, the start slot widens via sx (see SX_NO_ICON below) to
 *      reserve room for the absolute-positioned indicator next to the
 *      label. With a real startIcon, MUI does the swap itself and we
 *      pass through unchanged.
 *
 * Both defaults are overridable. Callers needing a different indicator
 * (e.g. on a dark-background button where currentcolor doesn't read
 * well) can pass their own `loadingIndicator`. Almost no one should.
 *
 * Going forward, prefer ActionButton over raw <LoadingButton> at every
 * action callsite — the wrapper is a no-cost abstraction (one extra
 * function component) that buys consistency for free.
 */

// Empty span that takes no space at idle. Acts as the anchor MUI Lab
// requires when loadingPosition="start" but no real startIcon is given.
// Without this, MUI silently centres the indicator over the label.
const NO_ICON = <span style={{ width: 0, height: 0 }} aria-hidden="true" />;

// sx applied only when our placeholder is in use (no real startIcon).
// Idle: zero out the start-icon wrapper's width and margins so the
//       button's idle width is identical to a button with no startIcon
//       prop at all.
// Loading: the start slot needs to actually take up space so MUI's
//       absolute-positioned indicator (`left: 14` for outlined/contained,
//       `left: 6` for text variant) sits beside the label rather than
//       on top of it. SquareDotsSpinner small is 24px wide; we reserve
//       1.5em which is close enough across all variants and inherits
//       the button's font sizing.
const SX_NO_ICON = {
    '&:not(.MuiLoadingButton-loading) .MuiButton-startIcon': {
        marginLeft: 0,
        marginRight: 0,
        width: 0
    },
    '&.MuiLoadingButton-loading .MuiButton-startIcon': {
        width: '1.5em'
    }
};

// Merge a caller-supplied sx (which may be an array, object, or undefined)
// with our internal override. Preserves MUI's array-form sx convention
// so theme-level overrides still cascade correctly.
function mergeSx(callerSx, override) {
    if (!callerSx) return override;
    if (Array.isArray(callerSx)) return [...callerSx, override];
    return [callerSx, override];
}

export default function ActionButton({
    loadingIndicator,
    loadingPosition,
    startIcon,
    sx,
    children,
    ...rest
}) {
    const hasRealStartIcon = startIcon != null;
    const resolvedIndicator = loadingIndicator
        ?? <SquareDotsSpinner size="small" aria-hidden="true" />;
    const resolvedPosition = loadingPosition ?? 'start';
    const resolvedSx = hasRealStartIcon ? sx : mergeSx(sx, SX_NO_ICON);

    return (
        <LoadingButton
            {...rest}
            sx={resolvedSx}
            startIcon={hasRealStartIcon ? startIcon : NO_ICON}
            loadingIndicator={resolvedIndicator}
            loadingPosition={resolvedPosition}
            >
            {children}
        </LoadingButton>
    );
}

ActionButton.propTypes = {
    // Override the default SquareDotsSpinner. Almost never needed.
    loadingIndicator: PropTypes.node,
    // Override the auto-derived position. Defaults to 'start' so the
    // indicator never overlaps the label. MUI accepts
    // 'start' | 'end' | 'center'.
    loadingPosition: PropTypes.oneOf(['start', 'end', 'center']),
    startIcon: PropTypes.node,
    // Forwarded to LoadingButton; merged with our internal placeholder
    // override when the caller didn't supply a real startIcon.
    sx: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])),
        PropTypes.func,
        PropTypes.object
    ]),
    children: PropTypes.node
};
